import { computed } from 'vue';
import type { ComputedRef, CSSProperties } from 'vue';
import { useGlobalConfigStore } from '@/stores';

interface EditorTooltipThemeOptions {
  /** Tooltip 内容最大宽度，长文本超过后由浮层自身换行。 */
  maxWidth?: string;
  /** Tooltip 内边距，侧栏窄提示可用更紧凑的值。 */
  padding?: string;
  /** Tooltip 文本行高，长版本名与短图标提示可分别控制。 */
  lineHeight?: string;
}

interface EditorTooltipThemeResult {
  /** Naive Tooltip 外层主题变量，负责真正的浮层背景与文字色。 */
  tooltipThemeOverrides: ComputedRef<Record<string, string>>;
  /** Tooltip 内容布局样式，只放尺寸相关属性，避免产生“外层 + 内层”两层背景。 */
  tooltipContentStyle: CSSProperties;
}

/**
 * 构建编辑器内 tooltip 的主题样式。
 *
 * Naive UI Tooltip 会 teleport 到 body 下，组件根节点上的 CSS 变量不会稳定继承。
 * 因此这里使用 themeOverrides 控制外层浮层颜色，只用 contentStyle 控制排版尺寸。
 */
export function useEditorTooltipTheme(options: EditorTooltipThemeOptions = {}): EditorTooltipThemeResult {
  const globalConfigStore = useGlobalConfigStore();
  const isDarkTheme = computed(() => Boolean(globalConfigStore.getProviderTheme()));
  const padding = options.padding ?? '7px 9px';
  const lineHeight = options.lineHeight ?? '1.45';

  const tooltipThemeOverrides = computed<Record<string, string>>(() => {
    if (!isDarkTheme.value) {
      return {
        color: '#ffffff',
        textColor: '#263548',
        borderRadius: '6px',
        boxShadow: '0 8px 24px rgb(15 23 42 / 16%)',
        padding,
      };
    }

    return {
      color: '#252b36',
      textColor: '#f3f7fb',
      borderRadius: '6px',
      boxShadow: '0 10px 28px rgb(0 0 0 / 32%)',
      padding,
    };
  });

  const tooltipContentStyle: CSSProperties = {
    fontSize: '12px',
    lineHeight,
  };

  if (options.maxWidth) {
    tooltipContentStyle.maxWidth = options.maxWidth;
  }

  return {
    tooltipThemeOverrides,
    tooltipContentStyle,
  };
}
